import {
  createApiClient,
  getCurrentWorkspaceId,
  setCurrentWorkspaceId,
  clearCurrentWorkspaceId,
} from './axios-instance';
import { BaseResponse } from './auth.service';

export interface TeamWorkspaceCreateRequest {
  code: string;
  name: string;
}

export interface WorkspaceResponse {
  id: string;
  type: 'team';
  code: string;
  name: string;
  owner_id: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

interface WorkspaceCodeCheckResponse {
  available: boolean;
  message: string;
}

export interface WorkspaceWithRole extends WorkspaceResponse {
  role: 'owner' | 'member';
}

export interface WorkspaceMember {
  id: string;
  account_id: string; // Account ID for seat assignments
  email: string | null;
  display_name: string | null;
  role: 'owner' | 'member';
  joined_at: string;
  is_current_user: boolean;
}

class WorkspaceService {
  /**
   * Create a new team workspace
   */
  async createTeamWorkspace(
    workspaceData: TeamWorkspaceCreateRequest,
    idToken: string
  ): Promise<WorkspaceResponse> {
    try {
      const client = createApiClient(idToken);
      const response = await client.post<BaseResponse<WorkspaceResponse>>(
        '/workspaces/team',
        workspaceData
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Team workspace creation failed');
      }

      return response.data.data!;
    } catch (error) {
      console.error('Team workspace creation failed:', error);
      throw error;
    }
  }

  /**
   * Check if workspace code is available
   */
  async checkWorkspaceCode(code: string): Promise<WorkspaceCodeCheckResponse> {
    try {
      const client = createApiClient();
      const response = await client.get<BaseResponse<WorkspaceCodeCheckResponse>>(
        `/workspaces/check-code/${encodeURIComponent(code)}`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Workspace code check failed');
      }

      return response.data.data!;
    } catch (error) {
      console.error('Workspace code check failed:', error);
      throw error;
    }
  }

  /**
   * Get all workspaces for the current user
   */
  async getUserWorkspaces(idToken: string): Promise<WorkspaceWithRole[]> {
    try {
      const client = createApiClient(idToken);
      const response = await client.get<BaseResponse<WorkspaceWithRole[]>>(
        '/workspaces/my-workspaces'
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get user workspaces');
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Failed to get user workspaces:', error);
      throw error;
    }
  }

  /**
   * Switch user's current workspace (client-side only)
   */
  switchWorkspace(workspaceId: string): void {
    setCurrentWorkspaceId(workspaceId);
  }

  /**
   * Get current workspace ID from localStorage
   */
  getCurrentWorkspaceId(): string | null {
    return getCurrentWorkspaceId();
  }

  /**
   * Clear current workspace from localStorage
   */
  clearCurrentWorkspace(): void {
    clearCurrentWorkspaceId();
  }

  /**
   * Get all members of a workspace
   * @param workspaceId - The workspace UUID
   * @param idToken - Authentication token
   * @param excludeOwner - If true, exclude workspace owner from results
   */
  async getWorkspaceMembers(
    workspaceId: string,
    idToken: string,
    excludeOwner: boolean = false
  ): Promise<WorkspaceMember[]> {
    try {
      const client = createApiClient(idToken);
      const params = excludeOwner ? { exclude_owner: true } : {};
      const response = await client.get<BaseResponse<WorkspaceMember[]>>(
        `/workspaces/${workspaceId}/members`,
        { params }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get workspace members');
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Failed to get workspace members:', error);
      throw error;
    }
  }
}

export const workspaceService = new WorkspaceService();
